import { useCallback, useEffect, useRef, useState } from 'react';

interface Options<D, P extends any[]> {
  ready?: boolean;
  initData?: D;
  auto?: boolean;
  defaultParams?: P;
}

export type BaseRequestFnType<D, P extends any[]> = (...args: P) => Promise<D>;

export function useRequest<D, P extends any[]>(requestFn: BaseRequestFnType<D, P>, options: Options<D, P> = {}) {
  const { initData = {}, auto, defaultParams = [] } = options;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<D>(initData as D);
  const [err, setErr] = useState<unknown>();
  const hasRun = useRef(false);
  const run = useCallback(async (...params: P) => {
    hasRun.current = true;
    setLoading(true);
    let res;
    try {
      res = await requestFn(...params);
      setData(res);
    } catch (error) {
      setErr(error);
    }
    setLoading(false);
    return res;
  }, []);

  useEffect(() => {
    if (auto) {
      run(...(defaultParams as P));
    }
  }, [auto]);

  return {
    run,
    loading,
    data,
    err,
  };
}
